import React, { useEffect, useState, useRef } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import {
  LogOut, Plus, Pencil, Trash2, X, Upload, Image as ImgIcon,
  ArrowUp, ArrowDown, Loader2, Save, ExternalLink, Film,
} from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["Gros Œuvre", "Second Œuvre", "Promotion Immobilière"];
const STATUSES = ["Terminé", "En cours", "VEFA en cours"];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const toAbsolute = (url) => (url?.startsWith("/api/") ? `${BACKEND_URL}${url}` : url);

const isVideoUrl = (u) => /\.(mp4|mov|webm|ogg)(\?|$)/i.test(u || "");

const emptyProject = {
  title: "",
  category: "Gros Œuvre",
  location: "",
  description: "",
  year: "",
  surface: "",
  status: "Terminé",
  images: [],
};

const AdminDashboard = () => {
  const { user, loading, logout } = useAuth();
  const [projects, setProjects] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [editing, setEditing] = useState(null); // project being edited or null
  const [showNew, setShowNew] = useState(false);

  const load = async () => {
    setFetching(true);
    try {
      const { data } = await api.get("/projects");
      // Tri du plus récent au plus ancien (par année, puis par ordre d'ajout)
      const sorted = [...data].sort((a, b) => {
        const yA = parseInt(a.year) || 0;
        const yB = parseInt(b.year) || 0;
        if (yB !== yA) return yB - yA;
        return (b.order || 0) - (a.order || 0);
      });
      setProjects(sorted);
    } catch (e) {
      toast.error("Erreur de chargement des projets");
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    if (user) load();
  }, [user]);

  if (loading) return null;
  if (!user) return <Navigate to="/admin/login" replace />;

  const handleCreate = async (payload) => {
    const { data } = await api.post("/projects", payload);
    toast.success("Projet créé");
    setShowNew(false);
    setProjects((prev) => [data, ...prev]);
    // Open editor to add images
    setEditing(data);
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Supprimer le projet "${p.title}" ? Cette action est irréversible.`)) return;
    try {
      await api.delete(`/projects/${p.id}`);
      toast.success("Projet supprimé");
      setProjects((prev) => prev.filter((x) => x.id !== p.id));
    } catch (e) {
      toast.error("Suppression échouée");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-dashboard">
      {/* Top bar */}
      <header className="bg-white border-b-2 border-yellow-400 shadow-sm sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-gradient-to-br from-yellow-500 to-yellow-400 rounded-lg flex items-center justify-center text-white font-bold">
              S
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">SOGI Admin</h1>
              <p className="text-xs text-gray-500">{user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="hidden md:inline-flex items-center gap-2 text-sm text-gray-600 hover:text-yellow-600 px-3 py-2"
              data-testid="admin-go-site"
            >
              <ExternalLink size={16} /> Voir le site
            </Link>
            <button
              onClick={logout}
              className="inline-flex items-center gap-2 bg-gray-900 text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-gray-800"
              data-testid="admin-logout-btn"
            >
              <LogOut size={16} /> Déconnexion
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-3xl font-bold text-gray-900">Gestion des réalisations</h2>
            <p className="text-gray-500 mt-1">
              {`${projects.length} projet${projects.length > 1 ? "s" : ""} — cliquez un projet pour ajouter / gérer les images`}
            </p>
          </div>
          <button
            onClick={() => setShowNew(true)}
            className="bg-gradient-to-r from-yellow-500 to-yellow-400 text-white font-bold px-5 py-3 rounded-xl hover:shadow-lg flex items-center gap-2"
            data-testid="admin-new-project-btn"
          >
            <Plus size={18} /> Nouveau projet
          </button>
        </div>

        {fetching ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="animate-spin text-yellow-500" size={40} />
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((p) => (
              <div
                key={p.id}
                className="bg-white rounded-2xl shadow-md border-2 border-gray-100 overflow-hidden hover:shadow-xl hover:border-yellow-200 transition-all"
                data-testid={`admin-project-card-${p.id}`}
              >
                <div className="relative h-48 bg-gray-100">
                  {p.images?.length > 0 ? (
                    isVideoUrl(p.images[0]) ? (
                      <div className="w-full h-full flex items-center justify-center bg-gray-800 text-white">
                        <Film size={36} />
                      </div>
                    ) : (
                      <img
                        src={toAbsolute(p.images[0])}
                        alt={p.title}
                        className="w-full h-full object-cover"
                      />
                    )
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      <ImgIcon size={36} />
                    </div>
                  )}
                  <div className="absolute top-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded-full">
                    {`${p.images?.length || 0} média${(p.images?.length || 0) > 1 ? "s" : ""}`}
                  </div>
                </div>
                <div className="p-5">
                  <div className="text-xs font-semibold text-yellow-600 mb-1">{p.category}</div>
                  <h3 className="font-bold text-gray-900 mb-1 truncate">{p.title}</h3>
                  <p className="text-sm text-gray-500 mb-4">
                    {p.location || "—"} · {p.year || "—"}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setEditing(p)}
                      className="flex-1 inline-flex items-center justify-center gap-1 bg-gray-900 text-white text-sm font-semibold py-2 rounded-lg hover:bg-gray-800"
                      data-testid={`admin-edit-${p.id}`}
                    >
                      <Pencil size={14} /> Gérer
                    </button>
                    <button
                      onClick={() => handleDelete(p)}
                      className="inline-flex items-center justify-center bg-red-50 text-red-600 border border-red-200 px-3 py-2 rounded-lg hover:bg-red-100"
                      data-testid={`admin-delete-${p.id}`}
                      title="Supprimer"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {showNew && (
        <ProjectEditor
          initial={emptyProject}
          isNew
          onClose={() => setShowNew(false)}
          onSubmit={handleCreate}
        />
      )}
      {editing && (
        <ProjectEditor
          initial={editing}
          onClose={() => setEditing(null)}
          onChanged={(updated) => {
            setProjects((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
            setEditing(updated);
          }}
        />
      )}
    </div>
  );
};

// ---------------- Project Editor Modal ----------------
const ProjectEditor = ({ initial, isNew = false, onClose, onSubmit, onChanged }) => {
  const [form, setForm] = useState({ ...initial });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      if (isNew) {
        await onSubmit({
          title: form.title, category: form.category, location: form.location,
          description: form.description, year: form.year, surface: form.surface,
          status: form.status, images: [],
        });
      } else {
        const payload = {
          title: form.title, category: form.category, location: form.location,
          description: form.description, year: form.year, surface: form.surface,
          status: form.status, images: form.images || [],
        };
        const { data } = await api.put(`/projects/${initial.id}`, payload);
        toast.success("Projet mis à jour");
        onChanged?.(data);
      }
    } catch (e) {
      toast.error("Échec de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || isNew) return;
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post(`/projects/${initial.id}/images`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, images: data.images }));
      toast.success(`${data.added.length} image(s) ajoutée(s)`);
      // Re-fetch parent list
      const { data: proj } = await api.get(`/projects/${initial.id}`);
      onChanged?.(proj);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Upload échoué");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleDeleteImage = async (url) => {
    if (!window.confirm("Supprimer cette image ?")) return;
    try {
      const { data } = await api.delete(`/projects/${initial.id}/images`, { data: { url } });
      setForm((f) => ({ ...f, images: data.images }));
      toast.success("Image supprimée");
      const { data: proj } = await api.get(`/projects/${initial.id}`);
      onChanged?.(proj);
    } catch (e) {
      toast.error("Suppression échouée");
    }
  };

  const moveImage = async (index, dir) => {
    const imgs = [...(form.images || [])];
    const newIdx = index + dir;
    if (newIdx < 0 || newIdx >= imgs.length) return;
    [imgs[index], imgs[newIdx]] = [imgs[newIdx], imgs[index]];
    setForm((f) => ({ ...f, images: imgs })); // optimistic
    try {
      await api.put(`/projects/${initial.id}/images/reorder`, { images: imgs });
      const { data: proj } = await api.get(`/projects/${initial.id}`);
      onChanged?.(proj);
    } catch (e) {
      toast.error("Réordonnancement échoué");
      // revert
      const { data: proj } = await api.get(`/projects/${initial.id}`);
      setForm((f) => ({ ...f, images: proj.images }));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-start md:items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full my-8 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="admin-project-editor"
      >
        <div className="bg-gradient-to-r from-yellow-500 to-yellow-400 px-6 py-4 flex items-center justify-between">
          <h3 className="text-lg font-bold text-white">
            {isNew ? "Nouveau projet" : `Modifier : ${initial.title}`}
          </h3>
          <button onClick={onClose} className="text-white/90 hover:text-white" data-testid="editor-close-btn">
            <X size={22} />
          </button>
        </div>

        <div className="p-6 space-y-6 max-h-[80vh] overflow-y-auto">
          {/* Form */}
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Titre *">
              <input
                type="text" value={form.title}
                onChange={(e) => update("title", e.target.value)}
                className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-yellow-500 focus:outline-none"
                data-testid="editor-field-title"
              />
            </Field>
            <Field label="Catégorie">
              <select
                value={form.category}
                onChange={(e) => update("category", e.target.value)}
                className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-yellow-500 focus:outline-none bg-white"
                data-testid="editor-field-category"
              >
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Localisation">
              <input
                type="text" value={form.location}
                onChange={(e) => update("location", e.target.value)}
                className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-yellow-500 focus:outline-none"
                data-testid="editor-field-location"
              />
            </Field>
            <Field label="Année">
              <input
                type="text" value={form.year}
                onChange={(e) => update("year", e.target.value)}
                className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-yellow-500 focus:outline-none"
                data-testid="editor-field-year"
              />
            </Field>
            <Field label="Surface">
              <input
                type="text" placeholder="ex: 320 m²" value={form.surface}
                onChange={(e) => update("surface", e.target.value)}
                className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-yellow-500 focus:outline-none"
                data-testid="editor-field-surface"
              />
            </Field>
            <Field label="Statut">
              <select
                value={form.status}
                onChange={(e) => update("status", e.target.value)}
                className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-yellow-500 focus:outline-none bg-white"
                data-testid="editor-field-status"
              >
                {STATUSES.map((s) => <option key={s}>{s}</option>)}
              </select>
            </Field>
            <div className="md:col-span-2">
              <Field label="Description">
                <textarea
                  rows={3} value={form.description}
                  onChange={(e) => update("description", e.target.value)}
                  className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-yellow-500 focus:outline-none resize-none"
                  data-testid="editor-field-description"
                />
              </Field>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              onClick={handleSave}
              disabled={saving || !form.title.trim()}
              className="inline-flex items-center gap-2 bg-gray-900 text-white font-bold px-5 py-2.5 rounded-xl hover:bg-gray-800 disabled:opacity-60"
              data-testid="editor-save-btn"
            >
              {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
              {isNew ? "Créer et continuer" : "Enregistrer"}
            </button>
          </div>

          {!isNew && (
            <>
              <hr className="border-gray-200" />
              {/* Image manager */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-bold text-gray-900">
                    {`Galerie (${form.images?.length || 0} média${(form.images?.length || 0) > 1 ? "s" : ""})`}
                  </h4>
                  <div>
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/*,video/mp4,video/quicktime,video/webm"
                      multiple
                      className="hidden"
                      onChange={handleUpload}
                      data-testid="editor-file-input"
                    />
                    <button
                      onClick={() => fileRef.current?.click()}
                      disabled={uploading}
                      className="inline-flex items-center gap-2 bg-gradient-to-r from-yellow-500 to-yellow-400 text-white font-bold px-4 py-2 rounded-xl hover:shadow-lg disabled:opacity-60"
                      data-testid="editor-upload-btn"
                    >
                      {uploading ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />}
                      {uploading ? "Envoi..." : "Ajouter images"}
                    </button>
                  </div>
                </div>

                {uploading && (
                  <div className="text-sm text-gray-500 mb-3">
                    Optimisation automatique en cours (max 1600px, qualité 85%)...
                  </div>
                )}

                {form.images?.length === 0 ? (
                  <div className="border-2 border-dashed border-gray-200 rounded-xl p-10 text-center text-gray-400">
                    <ImgIcon className="mx-auto mb-2" size={36} />
                    Aucune image. Cliquez "Ajouter images" pour commencer.
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {form.images.map((url, idx) => (
                      <div
                        key={url}
                        className="relative group bg-gray-100 rounded-xl overflow-hidden aspect-square"
                        data-testid={`editor-image-${idx}`}
                      >
                        {isVideoUrl(url) ? (
                          <div className="w-full h-full flex items-center justify-center bg-gray-800 text-white">
                            <Film size={32} />
                            <span className="absolute bottom-1 left-1 text-xs bg-black/70 px-2 py-0.5 rounded">vidéo</span>
                          </div>
                        ) : (
                          <img
                            src={toAbsolute(url)}
                            alt={`Image ${idx + 1}`}
                            className="w-full h-full object-cover"
                          />
                        )}
                        {idx === 0 && (
                          <span className="absolute top-1 left-1 bg-yellow-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">COUVERTURE</span>
                        )}
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2 p-2">
                          <div className="flex gap-1">
                            <button
                              onClick={() => moveImage(idx, -1)}
                              disabled={idx === 0}
                              className="bg-white text-gray-900 p-2 rounded-lg disabled:opacity-40 hover:bg-yellow-400 hover:text-white"
                              title="Monter"
                              data-testid={`editor-image-up-${idx}`}
                            >
                              <ArrowUp size={14} />
                            </button>
                            <button
                              onClick={() => moveImage(idx, 1)}
                              disabled={idx === form.images.length - 1}
                              className="bg-white text-gray-900 p-2 rounded-lg disabled:opacity-40 hover:bg-yellow-400 hover:text-white"
                              title="Descendre"
                              data-testid={`editor-image-down-${idx}`}
                            >
                              <ArrowDown size={14} />
                            </button>
                          </div>
                          <button
                            onClick={() => handleDeleteImage(url)}
                            className="bg-red-500 text-white text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-red-600 inline-flex items-center gap-1"
                            data-testid={`editor-image-del-${idx}`}
                          >
                            <Trash2 size={12} /> Supprimer
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="block text-sm font-semibold text-gray-700 mb-1.5">{label}</label>
    {children}
  </div>
);

export default AdminDashboard;
